
// Browser feature detection and compatibility utilities
interface BrowserSupportFeatures {
  speechRecognition: boolean;
  speechSynthesis: boolean;
  mediaDevices: boolean;
  webAudio: boolean;
}

export const checkBrowserSupport = (): BrowserSupportFeatures => {
  return {
    speechRecognition: typeof window !== 'undefined' && 
      !!(window.SpeechRecognition || window.webkitSpeechRecognition),
    speechSynthesis: typeof window !== 'undefined' && 'speechSynthesis' in window,
    mediaDevices: typeof navigator !== 'undefined' && 
      !!navigator.mediaDevices && 
      !!navigator.mediaDevices.getUserMedia,
    webAudio: typeof window !== 'undefined' && 'AudioContext' in window
  };
};

export const getBrowserName = (): string => {
  const userAgent = navigator.userAgent;
  if (userAgent.match(/chrome|chromium|crios/i)) return 'Chrome';
  if (userAgent.match(/firefox|fxios/i)) return 'Firefox';
  if (userAgent.match(/safari/i)) return 'Safari';
  if (userAgent.match(/opr\//i)) return 'Opera';
  if (userAgent.match(/edg/i)) return 'Edge';
  return 'Unknown Browser';
};

export const getSupportMessage = (features: BrowserSupportFeatures): string => {
  const browser = getBrowserName();
  if (!features.speechRecognition) {
    return `Voice commands are not supported in ${browser}. For the best experience, please use Chrome, Edge, or Safari.`;
  }
  if (!features.mediaDevices) {
    return `Microphone access is not supported in ${browser}. Please check your browser settings or try a different browser.`;
  }
  return '';
};

// Initialize speech recognition with fallbacks
export const initSpeechRecognition = (): SpeechRecognition | null => {
  if (typeof window === 'undefined') return null;
  
  try {
    // Try to get the standard or webkit-prefixed SpeechRecognition
    const SpeechRecognitionImpl = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognitionImpl) return null;
    
    const recognition = new SpeechRecognitionImpl();
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    recognition.lang = 'en-US';
    
    return recognition;
  } catch (error) {
    console.error('Failed to initialize speech recognition:', error);
    return null;
  }
};
